import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ISystemSettings } from '../../interfaces/system-settings';
import {
  IRescheduleOrder,
  IRescheduleOrders,
  ITaskInfo,
  ITaskNote,
  IVehicle,
} from '../../interfaces/task-info';
import { MapService } from '../../services';
import { BBWTMessageService } from '../../services';
import { OrderService } from '../../services';
import { SettingsService } from '../../services';
import { TaskService } from '../../services';
import { VehicleService } from '../../services';
import { IJobScreen, IOrderScreen } from '../../interfaces/job/job';
import { Router } from '@angular/router';
import moment from 'moment';

export interface TimeChange {
  days: number;
  hours: number;
  minutes: number;
  hasChanged: boolean;
}

@Component({
  selector: 'reschedule-task',
  templateUrl: 'reschedule-task.component.html',
  styleUrls: ['reschedule-task.component.scss'],
})
export class RescheduleTaskComponent implements OnInit {
  vehicle: IVehicle;
  isSearchVehicle: boolean = false;

  currentRouteTime: any;
  currentDistanceMiles: any;
  systemSettings: ISystemSettings;
  minutesInterval = 30; // default

  regNumber: string;
  pickupNotes: ITaskNote[];
  dropoffNotes: ITaskNote[];
  dateTimesChanged: boolean = false;
  showConfirmDates: any;

  previousDropoffEarly: Date;
  previousPickupEarly: Date;
  previousPickupLate: Date;
  previousDropoffLate: Date;

  diffPED: TimeChange = { days: 0, hours: 0, minutes: 0, hasChanged: false };
  diffPLD: TimeChange = { days: 0, hours: 0, minutes: 0, hasChanged: false };
  diffDED: TimeChange = { days: 0, hours: 0, minutes: 0, hasChanged: false };
  diffDLD: TimeChange = { days: 0, hours: 0, minutes: 0, hasChanged: false };

  savingReschedule: boolean = false;

  @Output() taskUpdated: EventEmitter<any>;
  @Output() newJob: EventEmitter<any>;
  @Input() buttonClass = 'ui-button ui-button-danger btn-to-right';

  private _pickupEarliestDateTime: Date;
  public set pickupEarliestDateTime(value: Date) {
    this.trimSeconds(value);

    this._pickupEarliestDateTime = this.roundUpTime(value);
    this.dateTimesChanged = true;

    this.calcTimeChanges(this.selectedTask.pickupEarliestDateTime, value, this.diffPED);
  }

  public get pickupEarliestDateTime(): Date {
    return this._pickupEarliestDateTime;
  }

  private _pickupLatestDateTime: Date;
  public set pickupLatestDateTime(value: Date) {
    this.trimSeconds(value);
    this._pickupLatestDateTime = value;
    this.dateTimesChanged = true;

    this.calcTimeChanges(this.selectedTask.pickupLatestDateTime, value, this.diffPLD);
  }

  public get pickupLatestDateTime(): Date {
    return this._pickupLatestDateTime;
  }

  private _dropoffEarliestDateTime: Date;
  public set dropoffEarliestDateTime(value: Date) {
    this.trimSeconds(value);

    this._dropoffEarliestDateTime = value;
    this.dateTimesChanged = true;

    this.calcTimeChanges(this.selectedTask.dropoffEarliestDateTime, value, this.diffDED);
  }

  public get dropoffEarliestDateTime(): Date {
    return this._dropoffEarliestDateTime;
  }

  private _dropoffLatestDateTime: Date;
  public set dropoffLatestDateTime(value: Date) {
    this.trimSeconds(value);

    this._dropoffLatestDateTime = value;
    this.dateTimesChanged = true;

    this.calcTimeChanges(this.selectedTask.dropoffLatestDateTime, value, this.diffDLD);
  }

  public get dropoffLatestDateTime(): Date {
    return this._dropoffLatestDateTime;
  }

  @Input('selectedTask') selectedTask: ITaskInfo;
  @Input('selectedOrder') selectedOrder: IOrderScreen;
  @Input('jobInfo') jobInfo: IJobScreen;

  showEditTaskDialog: boolean = false;

  constructor(
    public messageService: BBWTMessageService,
    private router: Router,
    private orderService: OrderService,
    private vehicleService: VehicleService,
    private taskService: TaskService,
    private mapService: MapService,
    private settingsService: SettingsService,
  ) {
    this.taskUpdated = new EventEmitter<any>();
  }

  ngOnInit() {
    this.settingsService.GetSystemSettings().then((data: ISystemSettings) => {
      this.systemSettings = data;
    });

    this.otherTasks = this.jobInfo?.orders?.reduce(
      (counter, { tasks }) => counter + tasks.length,
      0,
    );
  }

  editTask() {
    this.setModelValues();
    this.setRouteTime();

    this.vehicle = this.selectedTask.vehicle;

    this.showEditTaskDialog = true;
  }

  calcTimeChanges(day1, day2, displayVar: TimeChange) {
    const original = moment(day1);
    const newMoment = moment(day2);
    const duration = moment.duration(newMoment.diff(original));

    displayVar.hours = duration.hours();
    displayVar.days = duration.days();
    displayVar.minutes = duration.minutes();
    displayVar.hasChanged = duration.asMinutes() != 0;
  }

  setModelValues() {
    const t = this.selectedTask;

    this._pickupEarliestDateTime = moment(t.pickupEarliestDateTime).toDate();
    this._pickupLatestDateTime = moment(t.pickupLatestDateTime).toDate();
    this._dropoffEarliestDateTime = moment(t.dropoffEarliestDateTime).toDate();
    this._dropoffLatestDateTime = moment(t.dropoffLatestDateTime).toDate();

    this.pickupNotes = t.notes.filter((x) => x.isPickUp);
    this.dropoffNotes = t.notes.filter((x) => !x.isPickUp);
  }

  setRouteTime() {
    //console.log("Setting route time..");
    let task = this.selectedTask;

    let pickup = task.pickupLocation;
    let dropoff = task.dropoffLocation;
    this.currentDistanceMiles = this.mapService.getMiles(this.selectedTask.distance);

    const dto = this.mapService.GetRouteDTO(pickup, dropoff, this.pickupEarliestDateTime);
    this.mapService.GetRoute(dto).then((route) => {
      if (route != null) {
        this.currentRouteTime = route.duration;
      } else {
        console.log('Route time is null for: ', task);
      }
    });
  }

  alignDateTimes() {
    // ensure following dates are aligned with this one (i.e. they can't be earlier and must include travel time)

    this.pickupLatestDateTime = this.isDateEarlier(
      this.pickupLatestDateTime,
      this.pickupEarliestDateTime,
    )
      ? moment(this.pickupEarliestDateTime).toDate()
      : moment(this.pickupLatestDateTime).toDate();

    //console.log("New pickup latest datetime: ", this.pickupLatestDateTime);

    let dropoffEarly = this.addRouteTime(this.pickupEarliestDateTime);
    this.dropoffEarliestDateTime = this.isDateEarlier(this.dropoffEarliestDateTime, dropoffEarly)
      ? moment(dropoffEarly).toDate()
      : moment(this.dropoffEarliestDateTime).toDate();

    //console.log("New dropoff earliest datetime: ", this.dropoffEarliestDateTime);

    let dropoffLate = this.addRouteTime(this.pickupLatestDateTime);
    this.dropoffLatestDateTime = this.isDateEarlier(this.dropoffLatestDateTime, dropoffLate)
      ? moment(dropoffLate).toDate()
      : moment(this.dropoffLatestDateTime).toDate();

    this.selectedTask.jobScheduledDate = this.pickupEarliestDateTime;

    //console.log("New dropoff latest datetime: ", this.dropoffLatestDateTime);
  }

  isDateEarlier(date1, date2) {
    let isEarlier = moment(date1).isBefore(date2);
    //console.log("Testing is date [", date1, "] earlier than [", date2, "],   : ", isEarlier);

    return isEarlier;
  }

  getValidPickupEarliest() {
    let earliest = moment(this.pickupEarliestDateTime).toDate();
    let defEarliest = this.getDefaultPickupEarliestDateTime();

    if (earliest < defEarliest) {
      let newEarliest = this.roundUpTime(defEarliest);
      this.trimSeconds(newEarliest);

      return newEarliest;
    }

    this.trimSeconds(earliest);
    return earliest;
  }

  getValidPickupLatest(pickupEarliest: Date) {
    let latest = moment(this.pickupLatestDateTime).toDate();
    let defLatest = this.getDefaultPickupLatestDateTime(pickupEarliest);

    if (latest < defLatest) {
      let newLatest = this.roundUpTime(defLatest);
      this.trimSeconds(newLatest);

      return newLatest;
    }

    this.trimSeconds(latest);
    return latest;
  }

  getValidDropoffEarliest(pickupEarliest: Date) {
    let earliest = moment(this.dropoffEarliestDateTime).toDate();
    let defEarliest = this.addRouteTime(pickupEarliest);

    if (earliest < defEarliest) {
      let newEarliest = this.roundUpTime(defEarliest);
      this.trimSeconds(newEarliest);

      return newEarliest;
    }

    //console.log("Using existing dropoff earliest, ", earliest, "  -  ", defEarliest);

    this.trimSeconds(earliest);
    return earliest;
  }

  getValidDropoffLatest(pickupLatest: Date) {
    let latest = moment(this.dropoffLatestDateTime).toDate();
    let defLatest = this.addRouteTime(pickupLatest);

    if (latest < defLatest) {
      let newLatest = this.roundUpTime(defLatest);
      this.trimSeconds(newLatest);

      return newLatest;
    }

    this.trimSeconds(latest);
    return latest;
  }

  addRouteTime(date: Date): Date {
    //console.log("Adding route time: ", this.currentRouteTime, " to date: ", date);

    return this.roundUpTime(moment(date).add(this.currentRouteTime, 's').toDate());
  }

  getDefaultPickupEarliestDateTime(): Date {
    return new Date();
    //return moment().add(24, 'h').toDate();
  }

  getDefaultPickupLatestDateTime(pickupEarliestDateTime: Date): Date {
    let createOrderTimeDayStart = this.systemSettings.createOrderTimeDayStart;
    let createOrderTimeDayEnd = this.systemSettings.createOrderTimeDayEnd;
    let createOrderPickUpLatestPlusDays = this.systemSettings.createOrderPickUpLatestPlusDays;
    let createOrderAfterEndPickUpLatestPlusDays =
      this.systemSettings.createOrderAfterEndPickUpLatestPlusDays;

    let pickupEarliestDateTimeMoment = moment(pickupEarliestDateTime);
    if (createOrderTimeDayStart) {
      let createOrderTimeDayStartMoment = moment(pickupEarliestDateTime)
        .startOf('day')
        .add(createOrderTimeDayStart, 'h');

      // 1st case
      if (pickupEarliestDateTimeMoment.isSameOrBefore(createOrderTimeDayStartMoment)) {
        if (createOrderPickUpLatestPlusDays) {
          return moment(pickupEarliestDateTime)
            .startOf('day')
            .add(createOrderPickUpLatestPlusDays, 'd')
            .add(createOrderTimeDayStart, 'h')
            .toDate();
        }
      }
    }

    if (createOrderTimeDayEnd) {
      let createOrderTimeDayEndMoment = moment(pickupEarliestDateTime)
        .startOf('day')
        .add(createOrderTimeDayEnd + 12, 'h');

      // 2nd case
      if (pickupEarliestDateTimeMoment.isSameOrBefore(createOrderTimeDayEndMoment)) {
        if (createOrderPickUpLatestPlusDays) {
          return moment(pickupEarliestDateTime).add(createOrderPickUpLatestPlusDays, 'd').toDate();
        }
      }
      // 3rd case
      else {
        if (createOrderAfterEndPickUpLatestPlusDays) {
          return moment(pickupEarliestDateTime)
            .add(createOrderAfterEndPickUpLatestPlusDays, 'd')
            .toDate();
        }
      }
    }

    // default
    return moment(pickupEarliestDateTime).add(12, 'h').toDate();
  }

  roundUpTime(time: Date) {
    this.trimSeconds(time);

    //console.log("Current minutes interval: ", this.minutesInterval);
    if (time.getMinutes() % this.minutesInterval == 0 || time.getMinutes() == 0) {
      return time;
    }

    var min = this.minutesInterval;
    while (min < 60) {
      if (min >= time.getMinutes()) {
        break;
      }

      min += this.minutesInterval;
    }

    if (min > 60) {
      time.setMinutes(60);
    } else {
      time.setMinutes(0);
    }

    return time;
  }

  trimSeconds(dt: Date) {
    if (dt) {
      dt.setSeconds(0);
      dt.setMilliseconds(0);
    }
  }

  dialogSaveTaskClick() {
    //if any of the task dates/times are outside a range that would be acceptable for a new order
    //then prompt the user with a warning and ask them if they want an auto-completed version with corrected min dates/times.

    if (this.dateTimesChanged) {
      let pickupEarly = this.getValidPickupEarliest();
      let dropoffEarly = this.getValidDropoffEarliest(pickupEarly);

      // the late dates/times will default to valid from settings if selected but are also acceptable to be the same as early
      let pickupLate = this.getValidPickupLatest(pickupEarly);
      let dropoffLate = this.getValidDropoffLatest(pickupLate);

      //console.log("pickup early: ", this.pickupEarliestDateTime, " - late: ", this.pickupLatestDateTime, "  to  dropoff early: ", this.dropoffEarliestDateTime, " - late: ", this.dropoffLatestDateTime);
      //console.log("COMPARE TO pickup early: ", pickupEarly, " - late: ", pickupLate, "  to  dropoff early: ", dropoffEarly, " - late: ", dropoffLate);

      if (
        this.pickupEarliestDateTime < pickupEarly ||
        this.pickupLatestDateTime < pickupLate ||
        this.dropoffEarliestDateTime < dropoffEarly ||
        this.dropoffLatestDateTime < dropoffLate
      ) {
        this.showConfirmDates = true;

        this.previousPickupEarly = this.selectedTask.pickupEarliestDateTime;
        this.previousPickupLate = this.selectedTask.pickupLatestDateTime;

        this.previousDropoffEarly = this.selectedTask.dropoffEarliestDateTime;
        this.previousDropoffLate = this.selectedTask.dropoffLatestDateTime;

        /*
        this.confirmationService.confirm({
          message: "The dates/times of this Task are outside the valid range, do you want to update them to be in a range acceptable for an unfulfilled order?",
          header: 'Update Task',
          icon: 'pi pi-exclamation-triangle',
          acceptLabel: 'Edit Dates/Times',
          rejectLabel: 'Continue Anyway',
          accept: () => {
            // update dates/times to valid settings but don't save in case user wants to alter them further
            this.pickupEarliestDateTime = pickupEarly;
            this.pickupLatestDateTime = pickupLate;
            this.dropoffEarliestDateTime = dropoffEarly;
            this.dropoffLatestDateTime = dropoffLate;
          },
          reject: () => {
            // save the current data
            this.saveTaskData();
          }
        });

         */
      } else {
        this.saveTaskData();
      }
    } else {
      if (this.otherDataHasChanged) {
        this.saveTaskData();
      } else {
        this.showEditTaskDialog = false;
      }
    }
  }

  otherDataHasChanged(): boolean {
    let changes: boolean = false;
    changes = this.regNumber != this.selectedTask.vehicle.registrationNumber;

    if (!changes) {
      let pickupNotes = this.selectedTask.notes.filter((x) => x.isPickUp);
      let dropoffNotes = this.selectedTask.notes.filter((x) => x.isPickUp);

      changes = this.notesHaveChanged(pickupNotes, this.pickupNotes);
      if (!changes) changes = this.notesHaveChanged(dropoffNotes, this.dropoffNotes);
    }

    return changes;
  }

  notesHaveChanged(taskNotes: ITaskNote[], editNotes: ITaskNote[]): boolean {
    let changes: boolean = false;
    for (let i = 0; i < taskNotes.length; i++) {
      changes = taskNotes[i].description != editNotes[i].description;
      if (changes) break;
    }

    return changes;
  }

  // checks and confirmations where required before actually saving
  changeAllTasks: any;
  otherTasks: number = 0;

  saveTaskData() {
    // SEND A REQUEST TO CLONE THE TASK WITH NEW DATES AND ADD IT TO THE ORDER

    let rescheduleOrders = {
      jobId: this.jobInfo.id,
      orders: [],
    } as IRescheduleOrders;

    this.jobInfo.orders.forEach((order) => {
      order.tasks.forEach((task) => {
        // Just the one task or all tasks in this job
        if (task.id == this.selectedTask.id || this.changeAllTasks) {
          let pickupEarliestDateTime = moment(task.pickupEarliestDateTime).add(this.diffPED);
          let pickupLatestDateTime = moment(task.pickupLatestDateTime).add(this.diffPLD);
          let dropoffEarliestDateTime = moment(task.dropoffEarliestDateTime).add(this.diffDED);
          let dropoffLatestDateTime = moment(task.dropoffLatestDateTime).add(this.diffDLD);

          rescheduleOrders.orders.push({
            orderId: order.id,
            jobId: this.jobInfo.id,
            tasks: [
              {
                taskId: task.id,
                pickupEarliestDateTime: pickupEarliestDateTime.format('YYYY-MM-DD HH:mm'),
                pickupLatestDateTime: pickupLatestDateTime.format('YYYY-MM-DD HH:mm'),
                dropoffEarliestDateTime: dropoffEarliestDateTime.format('YYYY-MM-DD HH:mm'),
                dropoffLatestDateTime: dropoffLatestDateTime.format('YYYY-MM-DD HH:mm'),

                originalPickupEarliestDateTime: task.pickupEarliestDateTime,
                originalPickupLatestDateTime: task.pickupLatestDateTime,
                originalDropoffEarliestDateTime: task.dropoffEarliestDateTime,
                originalDropoffLatestDateTime: task.dropoffLatestDateTime,
              },
            ],
          } as IRescheduleOrder);
        }
      });
    });

    this.reschedule(rescheduleOrders);
  }

  reschedule(rescheduleOrders: IRescheduleOrders) {
    this.savingReschedule = true;

    this.orderService
      .rescheduleOrders(rescheduleOrders)
      .then((newJobId) => {
        if (newJobId) {
          this.router.routeReuseStrategy.shouldReuseRoute = () => false;
          this.router.navigate(['app/jobscreen/' + newJobId]);
          this.showEditTaskDialog = false;
          this.savingReschedule = false;
          setTimeout(() => {
            this.messageService.ShowSuccess('Save', 'Task was rescheduled');
          }, 2000);
        }
      })
      .catch(() => {
        this.messageService.ShowError('Save', 'Task saving error');
        this.savingReschedule = false;
        this.showEditTaskDialog = false;
      });
  }

  undoChanges() {
    this.showConfirmDates = false;
    this.pickupEarliestDateTime = this.previousPickupEarly;
    this.pickupLatestDateTime = this.previousPickupLate;
    this.dropoffEarliestDateTime = this.previousDropoffEarly;
    this.dropoffLatestDateTime = this.previousDropoffLate;
  }

  searchVehicle() {
    this.isSearchVehicle = true;

    // don't null out the vehicle as it can still be used when adding vehicle reg/weight manually
    // this.vehicle = null;
    var that = this;
    this.vehicleService
      .GetByRegNum(this.regNumber)
      .then((vehicle) => {
        if (vehicle.registrationDate) vehicle.registrationDate = new Date(vehicle.registrationDate);

        that.vehicle = vehicle;
        that.isSearchVehicle = false;
        if (vehicle == null) that.messageService.ShowWarning('', 'Vehicle not Found');
      })
      .catch(() => {
        // that.vehicle = null;
        that.isSearchVehicle = false;
        that.messageService.ShowWarning('', 'Vehicle not Found');
      });
  }

  getTravelTime(): string {
    return this.mapService.getTravelTimeFormatted(this.selectedTask.estimateTime);
  }

  trackByIndex(index: number, obj: any): any {
    return index;
  }
}
